import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, of, tap } from 'rxjs';
import { SessionData } from '../../core/session-data';
import { SessionService } from '../../core/session.service';
import { AlternativeCurrenciesTypes } from './models/alternative-currency/alternative-currencies-types.enum';
import { AlternativeCurrencyBalance } from './models/alternative-currency/alternative-currency-balance';

@Injectable({
  providedIn: 'root'
})
export class AlternativeCurrenciesService {

  private balancesCache: AlternativeCurrencyBalance[];
  private sessionData: SessionData;
  private isLoyaltyLoading: boolean;

  constructor(
    private sessionService: SessionService,
    private httpClient: HttpClient
  ) {
    this.sessionData = this.sessionService.getSessionData();
    this.isLoyaltyLoading = true;
    this.balancesCache = [];
  }

  public getIsLoyaltyLoading() {
    return this.isLoyaltyLoading;
  }

  public getBalance(type: string): Observable<AlternativeCurrencyBalance[]> {
    return this.getBalances(type)
      .pipe(map(balances => {
        const filteredBalances =
          balances.filter(b =>
            b.type.toLocaleLowerCase() === type.toLocaleLowerCase());
        return filteredBalances.length > 0 ? filteredBalances : null;
      }));
  }

  public getBalanceCached(type: AlternativeCurrenciesTypes)
    : AlternativeCurrencyBalance {
    const balances = this.balancesCache &&
      this.balancesCache.length > 0 &&
      this.balancesCache
        .filter(b => b.type.toLowerCase() === type.toLowerCase());
    return balances && balances.length > 0
      ? balances[0]
      : undefined;
  }

  public getBalancesCached(type: AlternativeCurrenciesTypes) {
    this.getBalances(type);
    return (this.balancesCache);
  }

  public get balances(): Observable<AlternativeCurrencyBalance[]> {
    return this.getBalances(null);
  }

  private getBalances(type: string, forceUpdate: boolean = false):
    Observable<AlternativeCurrencyBalance[]> {

    if (!forceUpdate && this.balancesCache.length > 0) {
      return of(this.balancesCache);
    }

    if (!forceUpdate && type == AlternativeCurrenciesTypes.Loyalty) {
      this.getLoyaltyBalanceFromWebVisistor();
      return of(this.balancesCache);
    }

    const typeParam = type ? `/${type}` : '';

    return this.httpClient
      .get<AlternativeCurrencyBalance[]>(
        this.sessionData && this.sessionData.apiUrl
        + '/alternativecurrencies/acbalances'
        + typeParam)
      .pipe(tap(balances =>
        balances.forEach(balance => this.updateBalancesCache(balance))
      ));
  }

  private getLoyaltyBalanceFromWebVisistor() {
    const loyaltyPoints = this.sessionData.memberInfo.alternative_hard_currencies
      .filter(x => x.type === AlternativeCurrenciesTypes.Loyalty)
      .map(x => <AlternativeCurrencyBalance>{
        balance: x.balance,
        conversionRate: x.conversion_rate,
        subtype: x.sub_type,
        type: x.type,
        vendor: x.vendor
      });
    this.updateBalancesCache(loyaltyPoints[0]);
    this.isLoyaltyLoading = false;
  }

  private updateBalancesCache(balance: AlternativeCurrencyBalance) {
    if (!balance)
      return;
    const index = this.balancesCache.findIndex(
      x => x.type === balance.type &&
        x.subtype == balance.subtype
    );
    const addOrUpdate = index === -1 ? 0 : 1;
    this.balancesCache.splice(index, addOrUpdate, balance);
  }

  private getForcedBalances(type: string):
    Observable<AlternativeCurrencyBalance[]> {
    return this.getBalances(type, true);
  }

  public refresh(type: AlternativeCurrenciesTypes)
    : Observable<AlternativeCurrencyBalance[]> {
    return this.getForcedBalances(type);
  }
}
